import { Injectable } from '@angular/core';
import { IMAGE_URL } from '@api-ui-app/src/app/mockup-studio/constants/mockups';
import { ScenesForApproval } from '@api-ui-app/src/app/mockup-studio/types/mockup-studio-templates-approval';
import { environment } from '@api-ui-app/src/environments/environment';
import { caller } from '@gelato-api-ui/core/api/caller';
import { callWithRetries } from '@gelato-api-ui/core/api/helpers/callWithRetries';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import { DesignStructure } from '@gelato-api-ui/core/designs/design-structure';
import {
  PreflightPreviewsArchiveRequest,
  PreflightPreviewsArchiveResponse,
} from '@gelato-api-ui/core/preflight/preflight-previews-download';
import { PreflightProductResolutionRequest } from '@gelato-api-ui/core/preflight/preflight-product-resolution-request';
import { PreflightProductResolutionResponse } from '@gelato-api-ui/core/preflight/preflight-product-resolution-response';
import { printAreaNamesSorted } from '@gelato-api-ui/core/print-areas/constants/print-area-name';
import { ProductVariantAdditionalParams } from '@gelato-api-ui/core/product-catalogue/product-variant-additional-params';
import * as Sentry from '@sentry/angular';
import { Observable, of, timer } from 'rxjs';
import { AjaxRequest } from 'rxjs/ajax';
import { catchError, map, mergeMap, takeWhile } from 'rxjs/operators';
import { isPreflightTaskActive } from '../helpers/isPreflightTaskActive';
import { serializeObject } from '../helpers/serializeObject';
import { PreflightImageFromJsonResponse } from '../preflight-image-from-json-response';
import { PreflightPdfInfoRequest } from '../preflight-pdf-info-request';
import { PreflightPdfInfoResponse } from '../preflight-pdf-info-response';
import { PreflightPdfPreflightRequest } from '../preflight-pdf-preflight-request';
import { PreflightPdfPreflightResponse } from '../preflight-pdf-preflight-response';
import { PreflightPdfPreviewRequest } from '../preflight-pdf-preview-request';
import {
  PreflightProductFitImagesRequest,
  PreflightProductFitPdfRequest,
} from '../preflight-product-fit-images-request';
import { PreflightProductFitImagesResponse } from '../preflight-product-fit-images-response';
import { PreflightProductImageWithDesignRequest } from '../preflight-product-image-with-design-request';
import { PreflightProductImageWithDesignResponse } from '../preflight-product-image-with-design-response';
import { PreflightProductMatchRequest } from '../preflight-product-match-request';
import { PreflightProductMatchResponse } from '../preflight-product-match-response';
import { PreflightProductMockupImageFromJsonRequest } from '../preflight-product-mockup-image_from_json-request';
import { PreflightProductPersonalizationResponse } from '../preflight-product-personalization-response';
import { PreflightProductPreviewRequest } from '../preflight-product-preview-request';
import { PreflightTaskStatusResponse } from '../preflight-task-status-response';
import { PreviewFileType } from '../preview-file-type.enum';

@Injectable({ providedIn: 'root' })
export class PreflightApiService {
  private get: <T>(url: string, options?: AjaxRequest) => Observable<any>;
  private post: <T>(url: string, options?: AjaxRequest) => Observable<any>;

  constructor(private authService: AuthService) {
    this.get = caller(this.baseUrl, 'GET', () => this.authService.getHttpHeaders());
    this.post = caller(this.baseUrl, 'POST', () => this.authService.getHttpHeaders());
  }

  get baseUrl(): string {
    return environment.baseUrls.preflight;
  }

  getPdfInfo(request: PreflightPdfInfoRequest): Observable<PreflightPdfInfoResponse> {
    return this.get<PreflightPdfInfoResponse>('/pdf/info', {
      body: request,
    }).pipe(map(data => data.data));
  }

  getProductResolution(request: PreflightProductResolutionRequest): Observable<PreflightProductResolutionResponse> {
    return this.get<PreflightProductResolutionResponse>('/product/resolution', {
      body: request,
    }).pipe(map(data => data.data));
  }

  getPdfPreviewUrl(request: PreflightPdfPreviewRequest): string {
    const query: string = serializeObject(request);

    return `${this.baseUrl}/pdf/preview?${query}`;
  }

  getPdfPreviewUrls(fileUrl: string, pageCount: number, dpi: number = 75): string[] {
    const urls: string[] = [];

    for (let i = 0; i < pageCount; i++) {
      const request: PreflightPdfPreviewRequest = {
        pdf_url: fileUrl,
        page_nr: i + 1,
        dpi,
      };

      urls.push(this.getPdfPreviewUrl(request));
    }

    return urls;
  }

  getPdfPreflightAsync(request: PreflightPdfPreflightRequest): Observable<PreflightPdfPreflightResponse> {
    return this.get<PreflightPdfPreflightResponse>('/pdf/preflight', {
      body: {
        ...request,
        wait: 0,
      },
    }).pipe(map(data => data.data));
  }

  getTaskStatus(taskId: string): Observable<PreflightTaskStatusResponse> {
    return this.get<PreflightPdfPreflightResponse>(`/task/${taskId}`).pipe(map(data => data.data));
  }

  getTaskStatusUntilFinished(taskId: string): Observable<PreflightTaskStatusResponse> {
    return timer(0, 500).pipe(
      mergeMap(() => this.getTaskStatus(taskId)),
      takeWhile((response: PreflightTaskStatusResponse): boolean => isPreflightTaskActive(response.state), true),
    );
  }

  getProductPreviewUrl(request: PreflightProductPreviewRequest): Observable<string> {
    if (!this.isProductPreviewRequestValid(request)) {
      this.trackInvalidProductPreviewRequest(request);

      return of(null);
    }

    const body = {
      type: 'scene',
      ...request,
    };

    if (request.image_url) {
      body.image_url = encodeURIComponent(request.image_url);
    }

    if (request.images_urls) {
      body.images_urls = encodeURIComponent(request.images_urls);
    }

    return this.get<Record<'url', string>>('/v1/product/preview', {
      body,
      headers: { 'Content-Type': 'application/json' },
    }).pipe(map(r => r.data.url));
  }

  /**
   * @deprecated should be removed during GAPI-13663
   * @param request PreflightProductPreviewRequest
   */
  getProductPreviewUrlDeprecated(request: PreflightProductPreviewRequest): string {
    if (!this.isProductPreviewRequestValid(request)) {
      this.trackInvalidProductPreviewRequest(request);

      return null;
    }

    const query: string = serializeObject({
      type: 'scene',
      ...request,
    });

    return `${this.baseUrl}/product/preview?${query}`;
  }

  getProductMatch(request: PreflightProductMatchRequest): Observable<PreflightProductMatchResponse> {
    return this.get<PreflightProductMatchResponse>('/product/match', {
      body: request,
    }).pipe(map(data => data.data));
  }

  getProductFitImages(request: PreflightProductFitImagesRequest): Observable<PreflightProductFitImagesResponse> {
    return this.get<PreflightProductFitImagesResponse>('/product/fit/images', {
      body: request,
    }).pipe(map(data => data.data));
  }

  getProductFitPdf(request: PreflightProductFitPdfRequest): Observable<PreflightProductFitImagesResponse> {
    return this.get<PreflightProductFitImagesResponse>('/product/fit/pdf', {
      body: request,
    }).pipe(map(data => data.data));
  }

  getImageFromJson(
    designStructure: DesignStructure,
    pageNr: number,
    width: number,
    height: number,
    format: PreviewFileType,
    removeSamples: boolean,
  ): Observable<PreflightImageFromJsonResponse> {
    return callWithRetries(() => {
      const query: string = serializeObject({
        page_nr: pageNr,
        width,
        height,
        format,
        remove_samples: Number(Boolean(removeSamples)),
      });

      return this.post<any>(`/product/image_from_json?${query}`, {
        body: designStructure,
      }).pipe(map(data => data.data));
    }).pipe(
      catchError(error => {
        Sentry.captureException(new Error('GetImageFromJsonFailed'), scope => {
          scope.setExtras({
            designStructure: JSON.stringify(designStructure),
            width,
            height,
            format,
            error,
          });

          return scope;
        });

        return of(null);
      }),
    );
  }

  requestPreviewsDownload(request: PreflightPreviewsArchiveRequest): Observable<PreflightPreviewsArchiveResponse> {
    return this.post<PreflightPreviewsArchiveResponse>('/product/previews/download', { body: request }).pipe(
      map(data => data.data),
    );
  }

  checkPreviewsDownloadTask(task_id: string): Observable<PreflightTaskStatusResponse> {
    return this.get<PreflightTaskStatusResponse>(`/task/${task_id}`).pipe(map(data => data.data));
  }

  getMockupImageFromJson({
    width,
    height,
    productUid,
    productImageUrl,
    structure,
    format,
    placeholderUrls,
  }: PreflightProductMockupImageFromJsonRequest): Observable<{ url: string }> {
    const object: {
      width: number;
      height: number;
      placeholder_product_uid?: string;
      placeholder_url?: string;
      format?: string;
      placeholder_urls?: string;
    } = {
      width,
      height,
    };

    if (format) {
      object.format = format;
    }

    if (productUid) {
      object.placeholder_product_uid = productUid;
    }

    if (productImageUrl) {
      object.placeholder_url = productImageUrl;
    }

    if (placeholderUrls) {
      object.placeholder_urls = placeholderUrls;
    }

    return callWithRetries(() =>
      this.post<any>(`/product/mockup/image_from_json?${serializeObject(object)}`, {
        body: structure,
      }).pipe(map(data => data.data)),
    ).pipe(
      catchError(error => {
        Sentry.captureException(new Error('GetMockupImageFromJsonFailed'), scope => {
          scope.setExtras({
            designStructure: JSON.stringify(structure),
            width,
            height,
            format,
            error,
          });

          return scope;
        });

        return of(null);
      }),
    );
  }

  getProductImage(
    productUid: string,
    design_url?: string,
    additionalParams?: ProductVariantAdditionalParams,
  ): Observable<PreflightProductImageWithDesignResponse> {
    const body: PreflightProductImageWithDesignRequest = {
      product_uid: productUid,
      width: additionalParams?.width,
      height: additionalParams?.height,
    };

    if (design_url) {
      body.image_url = design_url;
    }

    if (additionalParams?.scene) {
      body.scene = additionalParams.scene;
    }

    if (additionalParams?.placeholderUrls) {
      body.placeholder_urls = additionalParams.placeholderUrls;
    }

    return this.get<PreflightProductImageWithDesignResponse>(`/product/image?${serializeObject(body)}`).pipe(
      map(data => data.data),
    );
  }

  getDigitizedText(
    payload: { textElement: any; path: string; output_px_width: number },
    imageId: string | undefined,
  ): Observable<{ url: string; id: string }> {
    const body = {
      text_element: payload.textElement,
      path: payload.path,
    };

    let url = `/embroidery/text/digitize?output_px_width=${payload.output_px_width}`;
    if (imageId) {
      url += `&image_id=${imageId}`;
    }
    return this.post<{ url: string; id: string }>(url, {
      body,
    }).pipe(map(r => r.data));
  }

  getQuantizedAsset(imageUrl: string, numColors: number): Observable<{ url: string; colors: string[] }> {
    const body = {
      num_colors: numColors,
    };

    let url = `/image/quantize?image_url=${encodeURIComponent(imageUrl)}`;
    return this.post<{ url: string; colors: string[] }>(url, {
      body,
    }).pipe(map(r => r.data));
  }

  getDigitizedImage(
    payload: {
      imageUrl: string;
      colorOverrides: { [color: string]: string };
      rendered_mm_width: number;
      output_px_width: number;
      quantized_colors: string[];
    },
    imageId: string | undefined,
  ): Observable<{ url: string; id: string }> {
    const body = {
      replace: payload.colorOverrides,
      quantized_colors: payload.quantized_colors,
    };

    let url = `/embroidery/image/digitize?image_url=${encodeURIComponent(payload.imageUrl)}&rendered_mm_width=${
      payload.rendered_mm_width
    }&output_px_width=${payload.output_px_width}`;
    if (imageId) {
      url += `&image_id=${imageId}`;
    }
    return this.post<{ url: string; id: string }>(url, {
      body,
    }).pipe(map(r => r.data));
  }

  getPersonalizedDesign(design: DesignStructure, text: string): Observable<PreflightProductPersonalizationResponse> {
    return this.post(`/product/personalization_from_json?text=${text}`, {
      body: design,
    }).pipe(map(data => data?.data));
  }

  /**
   * Get the list of scenes with versions names for a given product
   */
  getScenes(productUid: string): Observable<{ scenes: ScenesForApproval }> {
    return this.get<{ scenes: ScenesForApproval }>(`/mockup/versions?product_uid=${productUid}`).pipe(
      map(data => data?.data),
    );
  }

  /**
   * Get scene version preview for review
   */
  getVersionPreview(
    productUid: string,
    sceneName: string,
    version: string,
    color: string,
    bg: string,
  ): Observable<{ url: string }> {
    // Image URls should contain all print areas and the some image URL to test print areas placement
    const imagesUrls = JSON.stringify(
      printAreaNamesSorted.reduce((acc, printArea) => {
        acc[printArea] = IMAGE_URL;
        return acc;
      }, {}),
    );

    return this.get<{ url: string }>(
      `/mockup/preview/version?product_uid=${productUid}&scene_name=${sceneName}&version=${version}&color=${color}&bg=${bg}&format=jpeg&images_urls=${imagesUrls}&fill_method=fill&height=2000&width=2000`,
    ).pipe(map(data => data?.data));
  }

  /**
   * Promote scene version mockup and make it the default one for a given scene
   */
  promoteVersion(productUid: string, sceneName: string, version: string): Observable<string> {
    return this.post<string>(
      `/mockup/promote?product_uid=${productUid}&scene_name=${sceneName}&version=${version}`,
    ).pipe(map(data => data?.data));
  }

  private isProductPreviewRequestValid(request: PreflightProductPreviewRequest): boolean {
    return Boolean(request?.pdf_url || request?.image_url || request?.images_urls);
  }

  private trackInvalidProductPreviewRequest(request: PreflightProductPreviewRequest) {
    Sentry.captureException(new Error('InvalidProductPreviewRequest'), scope => {
      scope.setExtras({ request });

      return scope;
    });
  }
}
